import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { ToastService } from '@/app/shared/ui/notifications/toast.service';
import { triggerBlobDownload } from '@/app/shared/utils/file-download.util';
import { HospitalAccountDownloadRequest } from '../models/hospital-account-download.model';
import { HospitalAccountDownloadRepository } from '../services/hospital-account-download.repository';
import { toHospitalAccountExportNotice } from './hospital-account-export-notices';

@Injectable()
export class HospitalAccountExportStore {
  private readonly repository = inject(HospitalAccountDownloadRepository);
  private readonly toasts = inject(ToastService);
  private subscription: Subscription | null = null;

  private readonly _isExporting = signal(false);
  readonly isExporting = this._isExporting.asReadonly();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.cancel());
  }

  export(request: HospitalAccountDownloadRequest): void {
    if (this._isExporting()) return;
    this._isExporting.set(true);

    this.subscription = this.repository.download(request).subscribe({
      next: (file) => triggerBlobDownload(file.blob, file.fileName),
      error: (cause: unknown) => {
        this.finish();
        const notice = toHospitalAccountExportNotice(cause);
        this.toasts[notice.tone](notice.title, {
          description: notice.description,
          persistent: true,
        });
      },
      complete: () => this.finish(),
    });
  }

  cancel(): void {
    this.subscription?.unsubscribe();
    this.finish();
  }

  private finish(): void {
    this.subscription = null;
    this._isExporting.set(false);
  }
}
